/**
 * A scheduled capture of the two resin silo weight displays.
 *
 * One row per hourly capture, and only when at least one display was actually read. A run
 * that fails — camera unreachable, both displays illegible — writes nothing. There is no
 * error column and no error row: the gap in the series IS the signal, and the display layer
 * carries the previous weight forward across it (see `fillForwardResinSiloReadings`), so a
 * missed hour reads as "unchanged" rather than as a hole. The reason lives in the container
 * log, which is where you would look anyway.
 *
 * Images are NOT kept for every run — hourly capture would pile up ~700 files a month for
 * no benefit. The reader crops each frame, sends the crop to Grok, and throws it away; only
 * the runs at the configured retain hours (7 AM and 4 PM) keep an image, and what they keep
 * is the raw camera frame. Everywhere else the two numbers stand on their own. An empty
 * `originalUrl` means "not retained", never "upload failed".
 *
 * A null value means the reader could not produce a trustworthy number for that silo while
 * the other side read fine. Nulls are never guessed at here.
 */
export interface ResinSiloReading {
    id: string;
    capturedOn: Date;
    createdOn: Date;
    /** The raw uncropped camera frame. Empty unless this run's image was retained. */
    originalUrl: string;
    /** Left display. Null when it could not be read. */
    resinSiloAValue: number | null;
    /** Right display. Null when it could not be read. */
    resinSiloBValue: number | null;
}

export function createEmptyResinSiloReading(args: {
    id: string;
    capturedOn: Date;
    createdOn?: Date;
    originalUrl?: string;
    resinSiloAValue?: number | null;
    resinSiloBValue?: number | null;
}): ResinSiloReading {
    return {
        id: args.id,
        capturedOn: args.capturedOn,
        createdOn: args.createdOn ?? new Date(),
        originalUrl: args.originalUrl ?? '',
        resinSiloAValue: args.resinSiloAValue ?? null,
        resinSiloBValue: args.resinSiloBValue ?? null,
    };
}

/** The pair of weights, from either a raw reading or a filled-forward point. */
type ResinSiloValues = Pick<ResinSiloReading, 'resinSiloAValue' | 'resinSiloBValue'>;

/** True when both displays produced a number. */
export function resinSiloReadingIsComplete(reading: ResinSiloValues): boolean {
    return reading.resinSiloAValue !== null && reading.resinSiloBValue !== null;
}

/** Combined resin on hand across both silos, or null if either side is unknown. */
export function resinSiloReadingTotal(reading: ResinSiloValues): number | null {
    if (reading.resinSiloAValue === null || reading.resinSiloBValue === null) return null;
    return reading.resinSiloAValue + reading.resinSiloBValue;
}

/**
 * A reading as the app should display it: same value fields, but a side that could not be
 * read holds the last known weight instead of a null.
 *
 * Silo weight is a level, not an event — if a display was unreadable the resin did not go
 * anywhere, so "same as before" is both the honest answer and the one that keeps charts and
 * usage math continuous. The `carriedForward` flags are there so the UI can say so rather
 * than implying a fresh measurement.
 *
 * Note this fills forward across nulls WITHIN rows. A run that failed outright has no row at
 * all, so its hour simply is not in the series — the previous point stands until the next
 * successful one, which is the same behaviour by a different route.
 */
export interface ResinSiloReadingPoint {
    /** The stored row, untouched — nulls and all. */
    reading: ResinSiloReading;
    capturedOn: Date;
    /** Left display, filled forward from the last successful read when this one failed. */
    resinSiloAValue: number | null;
    /** Right display, filled forward from the last successful read when this one failed. */
    resinSiloBValue: number | null;
    resinSiloACarriedForward: boolean;
    resinSiloBCarriedForward: boolean;
}

/**
 * Carry the last known weight forward across unreadable displays.
 *
 * Returns points in chronological order regardless of the input order, because filling
 * forward only means anything on a sorted series. Leading nulls stay null — there is
 * nothing earlier to carry.
 */
export function fillForwardResinSiloReadings(readings: ResinSiloReading[]): ResinSiloReadingPoint[] {
    const ordered = [...readings].sort((a, b) => new Date(a.capturedOn).getTime() - new Date(b.capturedOn).getTime());

    let lastA: number | null = null;
    let lastB: number | null = null;

    return ordered.map(reading => {
        const aMissing = reading.resinSiloAValue === null;
        const bMissing = reading.resinSiloBValue === null;
        if (!aMissing) lastA = reading.resinSiloAValue;
        if (!bMissing) lastB = reading.resinSiloBValue;

        return {
            reading,
            capturedOn: new Date(reading.capturedOn),
            resinSiloAValue: aMissing ? lastA : reading.resinSiloAValue,
            resinSiloBValue: bMissing ? lastB : reading.resinSiloBValue,
            resinSiloACarriedForward: aMissing && lastA !== null,
            resinSiloBCarriedForward: bMissing && lastB !== null,
        };
    });
}

/**
 * Local calendar date of a reading, YYYY-MM-DD.
 *
 * Replaces the stored `captureDate` column, which was the same information as `capturedOn`
 * in a second shape. Day grouping is a read-time concern, so it is computed at read time.
 */
export function resinSiloReadingLocalDate(capturedOn: Date | string, timezone = 'America/Chicago'): string {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date(capturedOn));
    const byType = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return `${byType['year']}-${byType['month']}-${byType['day']}`;
}
